import React from 'react';
import * as querystring from 'query-string';
import {
  Dialogs,
  dialogEvents,
  DisconnectOverlay,
  Messages,
  LoadingScreen,
  ConnectScreen,
} from './';
import { DebugPage } from './debug';
import { ScreenPage } from '../pages/screen';
import { ServerLogPage } from './server-log';
import { getSettingByKey } from '../util/configHelper';
import { logger } from '../api/logger';
import { AppContainer } from './appContainer';
import { inject, observer } from 'mobx-react';
import { Providers } from '../providers';
import { Button } from './button';
import { screenShareApi } from '../api/screen';
import { Router, RouteComponentProps } from '@reach/router';
import { injectGlobal } from '../styles/styled-components';
import { EventViewerPage } from '../pages/event-viewer';
import { RegistryEditorPage } from '../pages/registry-editor';
import { TaskPage } from '../pages/tasks';
import { ServicePage } from '../pages/services';
import { SystemPage } from '../pages/system';
import { UserStore } from '../store/system-stores';
import { AppStore } from '../store/app-store';
import { Stores } from '../store/stores';
import { FilePage } from '../pages/filesystem';
import { TerminalPage } from '../pages/terminal';

type AppOwnProps = { path: string };
type AppStoreProps = { userStore: UserStore; appStore: AppStore };
type AppProps = AppOwnProps & AppStoreProps & RouteComponentProps;

export type UrlParams = {
  device_uuid?: string;
  device_name?: string;
  customer_name?: string;
  asset_id?: string;
  prompt?: 'on' | 'off';
};

type AppComponentState = {
  showSettings?: boolean;
};

/**
 * Top level app component. Contains pretty much everything.
 * Connect screen, then current page, top bar, sidebar.
 */
class AppComponent extends React.Component<AppProps, AppComponentState> {
  state: AppComponentState = {};

  componentDidMount() {
    //Connect the component to the app and user stores on mount
    const deviceName = getSettingByKey('last-device-name');
    window.document.title =
      (deviceName || '').toUpperCase() + ' | ' + window.document.title;
    logger.trackOnetime('app_load');

    // Using unload instead of beforeunload, to not prevent browsers use page navigation cache
    // See https://developer.mozilla.org/en-US/docs/Web/Events/beforeunload for more details
    window.addEventListener('unload', () =>
      logger.trackOnetime('app_unload', undefined, {
        timeSince: 'app_connected',
      }),
    );

    const { prompt } = this.getQuery();
    if (prompt) {
      this.props.appStore.changeShowPresenceToUser(prompt === 'on');
    }
  }

  componentDidUpdate() {
    if (this.props.userStore.user) {
      logger.trackOnetime('app_connected');
      setTimeout(
        () => logger.trackOnetime('channel_status', { timeout: 10 }),
        10 * 1000,
      );
    }

    if (this.props.userStore.user && this.props.location.pathname === '/') {
      // debugger;
      const { asset_id } = this.getQuery();
      const url = !!asset_id ? `/tasks?asset_id=${asset_id}` : '/tasks';
      this.props.navigate(url, { replace: true });
    }
  }

  fullHeightIf(path: string) {
    return this.props.location.pathname == path ? { height: '100%' } : {};
  }

  confirmLogOut = () => {
    dialogEvents.dialog({
      title: 'Disconnect?',
      body: <p>Are you sure you want to disconnect?</p>,
      buttons: [
        <Button content="Yes" onClick={this.doLogOut} primary />,
        <Button content="No" onClick={() => {}} />,
      ],
    });
  };

  doLogOut = () => {
    screenShareApi.disconnect();

    // todo: do a more gentle reloading
    window.location.href = window.location.toString().split('?')[0];
    window.location.reload();
  };

  getQuery = (): UrlParams => {
    // This is for backwards compatibility of the link
    // (they previously used hash history and search part also was in hash)
    const { location } = this.props;
    const query = querystring.parse(location.search);
    return query['device_uuid'] || query['asset_id']
      ? query
      : this.getQueryFromHash();
  };

  getQueryFromHash = () => {
    const startOfSearch = location.hash.indexOf('?');

    if (startOfSearch === -1) {
      return {};
    }

    const search = location.hash.substring(startOfSearch, location.hash.length);

    return querystring.parse(search);
  };

  render() {
    const {
      children,
      userStore: { user },
      appStore,
    } = this.props;

    const customerName = getSettingByKey('last-customer-name');
    const deviceName = getSettingByKey('last-device-name');
    const { asset_id } = this.getQuery();

    // Connection flow. Show the connect screen until we have login confirmed.
    if (!appStore.auth.loggedIn) {
      const query = this.getQuery();
      return (
        <>
          <DisconnectOverlay />
          <Dialogs />
          <ConnectScreen params={query} />
        </>
      );
    }

    if (!user) {
      return (
        <>
          <DisconnectOverlay />
          <Dialogs />
          <LoadingScreen>Connecting to server</LoadingScreen>
        </>
      );
    }

    //Some other stuff, like the "disconnected" overlay and the message system that aren't strictly inner content.
    return (
      <AppContainer
        isSyncro={(process.env.SYNCRO as any) as boolean}
        onLogout={this.confirmLogOut}
        customerName={customerName}
        deviceName={deviceName}
        assetId={asset_id}
      >
        <Messages />
        <DisconnectOverlay />
        <Dialogs />
        {children}
      </AppContainer>
    );
  }
}

const App = inject<Stores, AppOwnProps, AppStoreProps, {}>(
  ({ userStore, appStore }) => ({
    userStore,
    appStore,
  }),
)(observer(AppComponent)) as React.ComponentClass<AppOwnProps>;

// /**
//  * Sidebar. Contains links to all the pages.
//  * Kiiiiiind of responsive, but small mode doesn't work very well.
//  */
// class Sidebar extends React.Component<
//   {
//     activePath: string;
//     debugMenu: boolean;
//     enableSettingsButton: boolean;
//   },
//   {
//     open: boolean;
//     modalSettingsOpened?: boolean;
//   }
// > {
//   constructor(props) {
//     super(props);
//     this.state = { open: false };
//   }
//   componentDidMount() {
//     this.setState({ open: false });
//   }
//   getActiveClassName(path: string) {
//     return this.getActive(path) ? 'active' : '';
//   }
//   getActive(path: string) {
//     return (
//       this.props.activePath == path ||
//       (this.props.activePath == '/' && path == '/tasks')
//     );
//   }
//   debugItem() {
//     if (this.props.debugMenu) {
//       return (
//         <NavItem
//           className={this.getActiveClassName('/debug')}
//           path="/debug"
//           icon="terminal.svg"
//           label="API Debug"
//           active={this.getActive('/debug')}
//         />
//       );
//     }
//     return null;
//   }
//   sidebarHeader() {
//     if (process.env.SYNCRO) {
//       return (
//         <div className="header">
//           <span className="sidebar-header-title">
//             {' '}
//             <img src={require('img/syncro-logo-temp.png')} height="25" /> &nbsp;
//             Syncro{' '}
//           </span>
//         </div>
//       );
//     } else {
//       return (
//         <div className="header">
//           <span className="sidebar-header-title">
//             {' '}
//             <img src={require('img/logo.svg')} height="25" /> &nbsp; KABUTO{' '}
//           </span>
//         </div>
//       );
//     }
//   }
//   settingsButton = () => {
//     const { modalSettingsOpened } = this.state;

//     return (
//       <li>
//         <Button
//           style={{
//             width: '100%',
//             display: 'inline-flex',
//             backgroundColor: 'transparent',
//             border: 0,
//           }}
//           onClick={() =>
//             this.setState(
//               state =>
//                 ({ modalSettingsOpened: !state.modalSettingsOpened } as any),
//             )
//           }
//         >
//           <img src={require(`icon/terminal.svg`)} />
//           <span className="tab-label">&nbsp; &nbsp; Settings</span>
//         </Button>
//         <ModalSettings show={modalSettingsOpened} />
//       </li>
//     );
//   };
//   sidebarContent() {
//     var sidebarBgClass = process.env.SYNCRO
//       ? 'syncro-sidebar'
//       : 'kabuto-sidebar';
//     const { enableSettingsButton } = this.props;

//     return (
//       <div className={`${sidebarBgClass} col-md-4`} data-spy="affix">
//         {this.sidebarHeader()}
//         <div className="navbar-spacer" />
//         <ul className="nav nav-pills nav-stacked">
//           {pages.map(({ route, icon, label }) => {
//             return (
//               <NavItem
//                 className={this.getActiveClassName(route)}
//                 path={route}
//                 icon={icon}
//                 label={label}
//                 key={route}
//                 active={this.getActive(route)}
//               />
//             );
//           })}
//           {this.debugItem()}
//           {enableSettingsButton && this.settingsButton()}
//         </ul>
//       </div>
//     );
//   }
//   modalSidebar = () => {
//     let toggleOpen = () => this.setState({ open: !this.state.open });
//     let contents;
//     /*
//         if (this.state.open) {
//             contents = <div>
//                 <Overlay onClick={toggleOpen}/>
//                 {this.sidebarContent()}
//             </div>
//             //contents = this.sidebarContent()
//         }
//         else {
//             contents = <div className="btn btn-default" style={{position: "fixed", zIndex: 10000, opacity: 0.6}} onClick={toggleOpen}>
//                 <Glyphicon glyph="menu-hamburger" />
//             </div>
//         }

//         */
//     contents = (
//       <div>
//         <div
//           className="btn btn-default"
//           style={{ position: 'fixed', zIndex: 10000, opacity: 0.6 }}
//           onClick={toggleOpen}
//         >
//           <Glyphicon glyph="menu-hamburger" />
//         </div>
//         {this.state.open ? <Overlay onClick={toggleOpen} /> : null}
//         <MoveRightTransition show={this.state.open}>
//           {this.sidebarContent()}
//         </MoveRightTransition>
//       </div>
//     );
//     return contents;
//   };
//   render() {
//     return (
//       <div>
//         <MediaQuery minWidth={786}>{this.sidebarContent()}</MediaQuery>
//         <MediaQuery maxWidth={785}>{this.modalSidebar()}</MediaQuery>
//       </div>
//     );
//   }
// }

export class RootRouter extends React.Component<{}, {}> {
  render() {
    return (
      <Providers>
        <Router>
          <App path="/">
            <TaskPage path="tasks" />
            <ServicePage path="services" />
            <EventViewerPage path="event-viewer" />
            <SystemPage path="info" />
            <FilePage path="filesystem" />
            <RegistryEditorPage path="registry-editor" />
            <ScreenPage path="screen" />
            <TerminalPage path="terminal" />
            <DebugPage path="debug" />
          </App>
        </Router>
      </Providers>
    );
  }
}

// Workaround with reach router providing div wrapper to disable their styles effects
// https://github.com/reach/router/issues/63#issuecomment-428050999
injectGlobal`
  div[role="group"][tabindex] {
    display: contents;
  }
`;
